import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Tooltip,
  TextField,
  InputAdornment,
  Divider,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Autorenew as AutorenewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { useToast } from '../context/ToastContext';
import { useGetClientMessageHistoryQuery, useGetClientMessagesQuery, useGetClientQuery, useRegenerateClientMessageMutation } from '../redux/services/clientApi';
import { useNavigate, useParams } from 'react-router-dom';

// Local storage keys
const STORAGE_KEY = 'clientMessagesFilters';

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const ClientMessages = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [openedMsg, setOpenedMsg] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  
  // Get saved filters on component mount
  const initialFilters = getSavedFilters();
  
  // Pagination state with localStorage initialization
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    return initialFilters.pageSize || 25;
  });
  
  // Loading states
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  
  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      pageSize: pageSize,
      search: searchQuery,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [pageSize, searchQuery]);
  
  // Load initial search query from saved filters
  useEffect(() => {
    if (initialFilters.client_id !== clientId) {
      return;
    }
    if (initialFilters.search) {
      setSearchQuery(initialFilters.search);
      setActiveSearchQuery(initialFilters.search);
    }
    setPageSize(initialFilters.pageSize || 25);
    // eslint-disable-next-line
  }, [clientId]);

  const {
    data: client,
    isLoading: isClientLoading,
  } = useGetClientQuery(clientId, {
    skip: !clientId,
  });

  const [regenerateMsg, { isLoading: isRegenerating }] = useRegenerateClientMessageMutation();

  const {
    data: messages,
    isLoading: isMessagesLoading,
    error: messagesError,
    refetch,
  } = useGetClientMessagesQuery({ clientId, search: activeSearchQuery, page: page + 1, limit: pageSize });

  // Safely extract the array of messages from the API response
  const messagesArray = useMemo(() => {
    if (!messages) return [];
    
    if (messages.messages && Array.isArray(messages.messages)) {
      return messages.messages;
    } else {
      console.warn('Unexpected messages data format:', messages);
      return [];
    }
    
  }, [messages]);

  const handleChangePage = async (e, value) => {
    setIsPaginationLoading(true);
    setPage(value);
    
    try {
      await refetch();
    } finally {
      setIsPaginationLoading(false);
    }
  };

  const handleChangePageSize = async (e) => {
    const value = e.target.value;
    setIsPaginationLoading(true);
    setPageSize(value);
    setPage(0);
    // Save to localStorage
    const filters = {
      ...getSavedFilters(),
      pageSize: value
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    
    try {
      await refetch();
    } finally {
      setIsPaginationLoading(false);
    }
  };

  // Handle search submit
  const handleSearchSubmit = async () => {
    setIsSearchLoading(true);
    setActiveSearchQuery(searchQuery);
    // Reset to first page when searching
    setPage(0);
    
    try {
      await refetch();
    } finally {
      setIsSearchLoading(false);
    }
  };
  
  // Handle pressing Enter in search field
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Handle clearing filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setPage(0);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleHistoryClick = (msg) => {
    setOpenedMsg((data) => {
      return data !== null && data === msg.id ? null : msg.id
    });
  };

  const handleRegenerateClick = (msg) => {
    setSelectedMsg(msg);
    setRegenerateDialogOpen(true);
  };

  const handleRegenerateMessage = async () => {
    try {
      // Call the regenerateMsg mutation
      await regenerateMsg({clientId, messageId: selectedMsg.id}).unwrap();
      
      // Show success message
      showToast('Message sent to regeneration', 'success');
      setRegenerateDialogOpen(false);
      
      // Refetch to update the list
      refetch();
    } catch (error) {
      console.error('Error regenerating message:', error);
      showToast(error.data?.message || 'Failed to regenerate message', 'error');
    }
  };

  const isLoading = isMessagesLoading || isPaginationLoading || isSearchLoading || isClientLoading || isRegenerating;

  // Only show loading state if we're fetching the initial data
  if (isMessagesLoading && !messages) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading messages...</Typography>
      </Box>
    );
  }

  // Add additional debugging for the error state
  if (messagesError) {
    return (
      <Typography variant="h4" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Error loading messages: {messagesError.status} {messagesError.data?.message || 'Unknown error'}
      </Typography>
    );
  }

  return (
    <Paper elevation={1} sx={{ width: '100%', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/clients')}
          sx={{ mr: 2 }}
        >
          Back to Clients
        </Button>
        <Typography variant="h4" component="h1">
          Client {client?.name ? `${client.name}` : ''} messages
        </Typography>
      </Box>      

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          sx={{ flex: 1, maxWidth: 500 }}
          variant="outlined"
          size="small"
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          sx={{ ml: 1 }}
          variant="contained"
          color="primary"
          onClick={handleSearchSubmit}
          disabled={isLoading}
        >
          {isSearchLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Search'
          )}
        </Button>

        {searchQuery && (
          <Button
            sx={{ ml: 1 }}
            variant="outlined"
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

        <Box sx={{ width: '100%', mt: 2 }}>
          {messagesArray.length === 0 && !isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
              <Typography color="text.secondary">No messages found</Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Box>
              {messagesArray.map((msg) => ( 
                <Box 
                  key={msg.id} 
                  component="div"
                  sx={{ width: '80%', marginLeft: msg.is_input === 0 ? 'auto' : '', alignItems: 'flex-start', borderRadius: 1, p: 2, mb: 2, bgcolor: msg.is_input === 0 ? 'grey.50' : 'grey.100' }}
                >
                  {msg.is_input === 1 && 
                  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 2, alignItems: 'center', mb: 1 }}>
                    <Avatar alt={msg.client.name} src={msg.client.avatar}>{msg.client.name.toUpperCase().substring(0, 1)}</Avatar>
                    <Typography component="div" variant="body2" sx={{ fontWeight: 'medium', display: 'flex', flex: 1 }}>{msg.client.name}</Typography>
                    <Stack spacing={1} direction="row">
                      <Tooltip title="Regenerate">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleRegenerateClick(msg)}
                        >
                          <AutorenewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {msg.history_count > 0 && <Tooltip title="History">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleHistoryClick(msg)}
                        >
                          {openedMsg === msg.id && <KeyboardArrowUpIcon fontSize="small" />}
                          {openedMsg !== msg.id && <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>}
                    </Stack>
                  </Box>}
                  

                  <Typography component="div">{msg.message}</Typography>
                  <Typography 
                    variant="caption"
                    component="div"
                    color="text.secondary" 
                    sx={{ 
                      textAlign: 'right',
                      fontSize: '0.75rem'
                    }}
                  >
                    {msg.created}
                  </Typography>

                  {openedMsg === msg.id && <ClientMessageHistory clientId={clientId} messageId={msg.id} />}
                  
                </Box>
              ))}
              </Box>

              <TablePagination
                component="div"
                count={messages?.total}
                rowsPerPageOptions={[25, 50, 100]}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangePageSize}
              />
            </Box>
          )}
        </Box>

      {/* Regenerate message Dialog */}
      <Dialog
        open={regenerateDialogOpen} 
        onClose={() => setRegenerateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Regenerate message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to regenerate the answer to this message?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleRegenerateMessage} 
            color="primary" 
            variant="contained"
            disabled={isRegenerating}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const ClientMessageHistory = ({clientId, messageId}) => {
  const {
    data: messages,
    isLoading: isMessagesLoading,
    error: messagesError
  } = useGetClientMessageHistoryQuery({ clientId, messageId });

  // Safely extract the array of messages from the API response
  const messagesArray = useMemo(() => {
    if (!messages) return [];
    
    if (messages.messages && Array.isArray(messages.messages)) {
      return messages.messages;
    } else {
      console.warn('Unexpected history messages data format:', messages);
      return [];
    }
    
  }, [messages]);

  // Only show loading state if we're fetching the initial data
  if (isMessagesLoading && !messages) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={20} />
        <Typography sx={{ ml: 2 }}>Loading history...</Typography>
      </Box>
    );
  }

  // Add additional debugging for the error state
  if (messagesError) {
    return (
      <Typography variant="body1" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Error loading history: {messagesError.status} {messagesError.data?.message || 'Unknown error'}
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {messagesArray.length === 0 && !isMessagesLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">No history found</Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          {messagesArray.map((msg) => (
            <Box key={msg.id}  sx={{ flex: 1, borderRadius: 1, p: 2, mb: 2, bgcolor: 'white' }}>
              <Typography variant="caption" component="div" color="text.secondary">
                {msg.updated}
              </Typography>
              <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 1 }}>
                Query: {msg.query_msg}
              </Typography>
              {msg.summary !== '' && <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 1 }}>
                Summary: {msg.summary}
              </Typography>}
              <Typography sx={{ fontWeight: 'medium', mt: 1 }}>
                {msg.resp_msg}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};



export default ClientMessages; 